import classNames from 'classnames';
import ConfirmChangesDialog from 'components/organisms/NavigationBlocker/ConfirmChangesDialog';
import { useNavigationBlocker } from 'components/organisms/NavigationBlocker';
import PageHeader from 'components/organisms/PageOutlines/PageHeader';
import PageWrapper from 'components/organisms/PageOutlines/PageWrapper';
import { Button, Spinner, TextField } from 'components/widgets';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Translate, withLocalize } from 'react-localize-redux';
import { updateNavigationSections } from '../actions/navigation';
import { getCanAccessFeature } from '../selectors/navigation';
import NavigationService from '../services/navigation';
import { trackMixpanelEvent } from '../utils/general';
import toaster from '../utils/toaster';
import WarningMessage from '../components/utilities/WarningMessage';
import CategoryPicker from './components/CategoryPicker';
import DescriptionField from './components/DescriptionField';
import FieldLabel from './components/FieldLabel';
import LogoUpload from './components/LogoUpload';
import WebsiteField from './components/WebsiteField';
import css from './page.css';
import service from './service';

const PARTNER_ONBOARDING_MIXPANEL_PREFIX = 'Partner Onboarding';
const translationPrefix = 'STATIC.PAGES.PARTNER_INFORMATION';
const MAX_CATEGORIES = 2;
const PARTNER_INTEGRATION_PATH = '#/react/partners-integration';

const INITIAL_PROFILE = {
    companyName: '',
    website: '',
    description: '',
    categories: [],
    logoUrl: '',
};

const hasRequiredPartnerFields = ({ website, hasLogo }) => Boolean((website || '').trim()) && Boolean(hasLogo);

const navigationService = new NavigationService();

const PartnerInformation = ({ translate }) => {
    const dispatch = useDispatch();
    const canAccessIntegration = useSelector(getCanAccessFeature('partners_integration'));
    const [profile, setProfile] = useState(INITIAL_PROFILE);
    const [isLoading, setIsLoading] = useState(true);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [logoFile, setLogoFile] = useState(null);
    const [logoError, setLogoError] = useState('');
    const [saveMessage, setSaveMessage] = useState(null);
    const [websiteFieldValid, setWebsiteFieldValid] = useState(false);
    const savedProfileRef = useRef(INITIAL_PROFILE);
    const websiteFieldRef = useRef(null);
    const shouldRedirectAfterFirstSaveRef = useRef(false);

    useEffect(() => {
        service
            .getPartnerProfile()
            .then(data => {
                const profileData = {
                    companyName: data.company_name || '',
                    website: data.website || '',
                    description: data.description || '',
                    categories: data.categories || [],
                    logoUrl: data.logo_url || '',
                };
                const isInitiallyValid = hasRequiredPartnerFields({
                    website: profileData.website,
                    hasLogo: Boolean(profileData.logoUrl.trim()),
                });
                shouldRedirectAfterFirstSaveRef.current = !isInitiallyValid;
                setProfile(profileData);
                savedProfileRef.current = profileData;
            })
            .catch(() => toaster.error(translate(`${translationPrefix}.MESSAGES.LOAD_ERROR`)))
            .finally(() => setIsLoading(false));
    }, []);

    const setField = (field, value) => setProfile(prev => ({ ...prev, [field]: value }));

    const hasProfileChanges =
        profile.website !== savedProfileRef.current.website ||
        profile.description !== savedProfileRef.current.description ||
        profile.categories.length !== savedProfileRef.current.categories.length ||
        !profile.categories.every(c => savedProfileRef.current.categories.includes(c));

    const hasChanges = hasProfileChanges || Boolean(logoFile) || profile.logoUrl !== savedProfileRef.current.logoUrl;

    const { allowNextNavigation } = useNavigationBlocker(hasChanges);

    const confirmCancel = () => {
        setShowCancelConfirm(false);
        setProfile(savedProfileRef.current);
        setLogoFile(null);
        setLogoError('');
        websiteFieldRef.current?.reset();
    };

    const isFormValid =
        hasRequiredPartnerFields({
            website: profile.website,
            hasLogo: Boolean(logoFile || profile.logoUrl.trim()),
        }) &&
        websiteFieldValid &&
        !logoError;

    const handleSave = async () => {
        setIsSaving(true);
        setSaveMessage(null);

        try {
            if (logoFile) {
                const data = await service.uploadPartnerLogo(logoFile);
                setField('logoUrl', data.logo_url);
                setLogoFile(null);
            }

            if (hasProfileChanges) {
                const data = await service.updatePartnerProfile({
                    website: profile.website.trim(),
                    description: profile.description.trim(),
                    categories: profile.categories,
                });
                const updated = {
                    companyName: data.company_name || '',
                    website: data.website || '',
                    description: data.description || '',
                    categories: data.categories || [],
                    logoUrl: data.logo_url || '',
                };
                setProfile(updated);
                savedProfileRef.current = updated;
            } else {
                setProfile(prev => {
                    savedProfileRef.current = prev;
                    return prev;
                });
            }

            setSaveMessage({
                type: 'success',
                message: `${translationPrefix}.MESSAGES.SAVE_SUCCESS`,
            });

            trackMixpanelEvent(PARTNER_ONBOARDING_MIXPANEL_PREFIX, 'Partner Info Saved', {
                website: profile.website.trim(),
                description: profile.description.trim(),
                categories_selected: profile.categories,
            });

            // Refresh sidebar so the "Missing Info" tag updates after save
            navigationService
                .getNavigation({ cache: false })
                .then(sections => dispatch(updateNavigationSections(sections)))
                .catch(() => {});

            if (shouldRedirectAfterFirstSaveRef.current && canAccessIntegration) {
                shouldRedirectAfterFirstSaveRef.current = false;
                allowNextNavigation();
                window.location.replace(PARTNER_INTEGRATION_PATH);
                return;
            }
            setIsSaving(false);
        } catch (err) {
            setIsSaving(false);

            const errorMessage = typeof err === 'string' ? err : err?.value;
            setSaveMessage({
                type: 'error',
                message: errorMessage || `${translationPrefix}.MESSAGES.SAVE_ERROR`,
            });
        }
    };

    const handleLogoFileChange = file => {
        setLogoError('');
        setLogoFile(file);
    };

    const handleLogoRemove = () => {
        setField('logoUrl', '');
        setLogoFile(null);
        setLogoError('');
    };

    const toggleCategory = category => {
        const { categories } = profile;
        if (categories.includes(category)) {
            setField(
                'categories',
                categories.filter(c => c !== category)
            );
        } else if (categories.length < MAX_CATEGORIES) {
            setField('categories', [...categories, category]);
        }
    };

    return (
        <PageWrapper className={css.wrapper} disablePadding>
            <PageHeader
                text={`${translationPrefix}.PAGE_TITLE`}
                subText={{
                    subTitle: `${translationPrefix}.PAGE_SUBTITLE`,
                }}
                shouldAddPadding
                className={css.pageHeader}
            />
            {isLoading ? (
                <Spinner expanded show />
            ) : (
                <>
                    {saveMessage && (
                        <div className={css.saveMessageContainer}>
                            <WarningMessage
                                type={saveMessage.type}
                                message={saveMessage.message}
                                className={css.saveMessage}
                                showIcon
                                onClose={() => setSaveMessage(null)}
                            />
                        </div>
                    )}
                    <div className={css.form}>
                        <div className={css.fieldGroup}>
                            <FieldLabel icon="building" translationKey={`${translationPrefix}.COMPANY_NAME_LABEL`} />
                            <TextField
                                value={profile.companyName}
                                onChange={() => {}}
                                disabled
                                containerClass={css.textFieldContainer}
                            />
                            <p className={css.fieldNote}>
                                <Translate id={`${translationPrefix}.COMPANY_NAME_NOTE`} />
                            </p>
                        </div>
                        <div className={css.fieldGroup}>
                            <WebsiteField
                                ref={websiteFieldRef}
                                value={profile.website}
                                savedWebsite={savedProfileRef.current.website}
                                onChange={value => setField('website', value)}
                                onValidityChange={valid => setWebsiteFieldValid(valid)}
                                translationPrefix={translationPrefix}
                                containerClass={css.textFieldContainer}
                                translate={translate}
                            />
                        </div>
                        <div className={css.fieldGroup}>
                            <FieldLabel icon="upload" translationKey={`${translationPrefix}.LOGO_LABEL`} required />
                            <p className={css.fieldNote}>
                                <Translate id={`${translationPrefix}.LOGO_NOTE`} />
                            </p>
                            <LogoUpload
                                logoFile={logoFile}
                                logoUrl={profile.logoUrl}
                                error={logoError}
                                onFileChange={handleLogoFileChange}
                                onValidationError={setLogoError}
                                onRemove={handleLogoRemove}
                            />
                        </div>
                        <div className={css.fieldGroup}>
                            <FieldLabel
                                icon="file-description"
                                translationKey={`${translationPrefix}.DESCRIPTION_LABEL`}
                            />
                            <p className={css.fieldNote}>
                                <Translate id={`${translationPrefix}.DESCRIPTION_NOTE`} />
                            </p>
                            <DescriptionField
                                value={profile.description}
                                onChange={value => setField('description', value)}
                            />
                        </div>
                        <div className={classNames(css.fieldGroup, css.categoriesFieldGroup)}>
                            <FieldLabel icon="layout-grid" translationKey={`${translationPrefix}.CATEGORIES_LABEL`} />
                            <p className={css.fieldNote}>
                                <Translate id={`${translationPrefix}.CATEGORIES_NOTE`} data={{ max: MAX_CATEGORIES }} />
                            </p>
                            <CategoryPicker
                                selected={profile.categories}
                                max={MAX_CATEGORIES}
                                onToggle={toggleCategory}
                            />
                        </div>
                    </div>
                    <div className={css.footer}>
                        {(!isFormValid || !hasChanges) && (
                            <span className={css.footerNote}>
                                <Translate
                                    id={
                                        !isFormValid
                                            ? `${translationPrefix}.REQUIRED_FIELDS_NOTE`
                                            : `${translationPrefix}.NO_CHANGES_NOTE`
                                    }
                                />
                            </span>
                        )}
                        {hasChanges && (
                            <Button
                                type="flat"
                                onClick={() => setShowCancelConfirm(true)}
                                disabled={isSaving}
                                text="STATIC.BUTTONS.CANCEL"
                            />
                        )}
                        <Button
                            type="primary"
                            onClick={handleSave}
                            disabled={!hasChanges || !isFormValid || isSaving}
                            showSpinner={isSaving}
                            text="STATIC.BUTTONS.SAVE"
                        />
                    </div>
                </>
            )}
            <ConfirmChangesDialog
                open={showCancelConfirm}
                onAccept={confirmCancel}
                onReject={() => setShowCancelConfirm(false)}
            />
        </PageWrapper>
    );
};

PartnerInformation.propTypes = {
    translate: PropTypes.func.isRequired,
};

export default withLocalize(PartnerInformation);
